import { useQueryClient } from '@tanstack/react-query';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { SignatureRestoreAccessConfirmModal } from '../../../../components/signature-restore-access-confirm-modal';
import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { HttpException } from '../../../../exceptions/index';
import { PendingMutationSettleOutcome } from '../../../../hooks/event/pending-events.store';
import {
  EventHooks,
  useIdempotentAppMutation,
  useLocale,
  useReactForm,
  useRevokedAccessBeforeSend,
} from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import {
  EventFormType,
  EventTypeBackend,
  eventTypeMapping,
  EventTypeTitle,
} from '../../../../types/events.types';
import { handleFormInvalidField } from '../../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { eventModalStore } from '../../desktop/desktop.store';
import { describeInvalidFormField } from '../describe-invalid-form-field';
import { ChildrenFormProps } from '../event-form.types';
import { FormSchema, formSchema, GrantSignatureOption } from '../event-form.validation';
import { BulkGrantStepFirst } from '../step-components/bulk-grant-steps/bulk-grant-step-first';
import { BulkGrantStepFourth } from '../step-components/bulk-grant-steps/bulk-grant-step-fourth';
import { BulkGrantStepThird } from '../step-components/bulk-grant-steps/bulk-grant-step-third';
import { GrantStepFirst } from '../step-components/grant-steps/grant-step-first';
import { GrantStepFourth } from '../step-components/grant-steps/grant-step-fourth';
import { GrantStepThird } from '../step-components/grant-steps/grant-step-third';
import {
  CompanyIncorporationFinalStep,
  CompanyIncorporationStepOne,
  CompanyIncorporationStepThree,
  CompanyIncorporationStepTwo,
} from '../step-components/index';
import { FinalStep } from '../steps/final-step';
import { InitialStep, StepOne } from '../steps/index';
import { OptionalStep } from '../steps/optional-step';
import { StepTwo } from '../steps/step-two';
import {
  mapCompanyIncorporationFormToCreateDto,
  resolveCompanyIncorporationManualFileLinks,
} from '../utils/company-incorporation-event-form.util';
import { mapReverseVestingFormToDto } from '../utils/reverse-vesting-form.util';

export type AddEventFormProps = {
  companyId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  issuedShares: number;
};

const defaultFormSteps: ((props: ChildrenFormProps) => ReactNode)[] = [
  (props) => <InitialStep {...props} />,
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <FinalStep {...props} />,
];

const grantFormStepsWithoutSignature: ((props: ChildrenFormProps) => ReactNode)[] = [
  (props) => <InitialStep {...props} />,
  (props) => <GrantStepFirst {...props} />,
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <FinalStep {...props} />,
];

const buildSanitizedGrantSignatoryAssignments = (
  signatoryAssignments:
    | Record<string, { fullName?: string; id: string } | null | undefined>
    | undefined,
): Record<string, { id: string }> => {
  return Object.fromEntries(
    Object.entries(signatoryAssignments ?? {})
      .filter(([, assignment]) => assignment?.id)
      .map(([signatoryTypeId, assignment]) => [signatoryTypeId, { id: assignment!.id }]),
  );
};

export const AddEventForm: FC<AddEventFormProps> = ({
  companyId,
  issuedShares,
  handleClose,
  invalidateQuery,
}) => {
  const type = eventModalStore(({ type }) => type);
  const [currentStep, setCurrentStep] = useState(type ? 2 : 1);
  const { messagesLocale } = useLocale();

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    getFieldState,
    setError,
    clearErrors,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
    mode: 'onChange',
    defaultValues: {
      initialStep: { type: type || undefined },
    },
  });

  const queryClient = useQueryClient();
  const { initialStep, stepOne } = useWatch<FormSchema>({ control });
  const grantData = stepOne?.eventDetails?.grant;
  // const companyAddEventTracker = AnalyticHooks.useClick(PossibleAction.ADD_EVENT);
  const { createAsync, isPending } = EventHooks.useCreate();
  const {
    checkAndProceed: runRevokedAccessCheck,
    clearState: clearRevokedAccessModalState,
    handleConfirmRestore,
    isChecking: isCheckingRevokedAccess,
    revokedSigners: revokedAccessSigners,
  } = useRevokedAccessBeforeSend();
  const [formSteps, setFormSteps] = useState(() =>
    type === EventFormType.GRANT ? grantFormStepsWithoutSignature : defaultFormSteps,
  );
  const { callback } = ApiService.uploadFile();

  const { mutate: uploadBulkGrant } = useIdempotentAppMutation(
    [QueryKey.GRANT_BULK_IMPORT, companyId],
    {
      defaultErrorHandling: false,
      mutationFn: async (
        {
          file,
          planId,
          additionalNotes,
          filesLinks,
          selectedTemplateId,
          signatoryAssignments,
        }: {
          additionalNotes?: string;
          file: File;
          filesLinks?: string[];
          planId: string;
          selectedTemplateId?: string;
          signatoryAssignments?: Record<string, { fullName: string; id: string } | null>;
        },
        { headers },
      ) => {
        const normalizedSignatoryAssignments =
          signatoryAssignments &&
          Object.fromEntries(
            Object.entries(signatoryAssignments)
              .filter(([, v]) => v?.id)
              .map(([k, v]) => [k, { id: v!.id }]),
          );
        const payload = {
          planId,
          additionalNotes,
          filesLinks,
          ...(selectedTemplateId &&
            normalizedSignatoryAssignments &&
            Object.keys(normalizedSignatoryAssignments).length > 0 && {
              selectedTemplateId,
              signatoryAssignments: normalizedSignatoryAssignments,
            }),
        };
        return callback(
          {
            endpoint: BackendRoute.COMPANIES,
            routePath: [companyId, 'events', 'bulk-grant'],
          },
          ['file', file],
          { formFields: { payload: JSON.stringify(payload) }, headers },
        );
      },
      onSuccess: async (_data, variables) => {
        invalidateQuery();
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY_FILES] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_REPOSITORY] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_FILES] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDER] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDERS] });
        if (variables.selectedTemplateId) {
          queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SIGNATURES] });
          queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SELECTED_COMPANY] });
        }
        toast.success(messagesLocale['GRANTS_SUCCESSFULLY_IMPORTED']);
      },
      onError: async (error) => {
        try {
          if (!(error instanceof HttpException)) {
            toast.error(messagesLocale['UNEXPECTED_ERROR_OCCURRED']);
            return;
          }

          type BulkGrantValidationError = {
            children?: Array<{
              children: Array<{
                children: Array<{
                  constraints: Record<string, string>;
                  property: string;
                }>;
              }>;
            }>;
            property?: string;
            value?: unknown;
          };

          const errorMessage = error.body?.message as
            | BulkGrantValidationError[]
            | string
            | undefined;

          if (Array.isArray(errorMessage)) {
            errorMessage.forEach((err) => {
              if (err?.children?.length && err.children[0]?.children?.[0]?.children?.[0]) {
                const childError = err.children[0].children[0].children[0]!;
                const constraintKey = Object.keys(childError.constraints)[0];
                const constraintMessage = childError.constraints[constraintKey];
                toast.error(`Error in property "${childError.property}": ${constraintMessage}`);
              } else {
                toast.error(`Error in property "${err.property}": ${err.value}`);
              }
            });
          } else if (typeof errorMessage === 'object' && errorMessage !== null) {
            toast.error(JSON.stringify(errorMessage));
          } else if (typeof errorMessage === 'string') {
            toast.error(errorMessage);
          } else {
            toast.error(error.message);
          }
        } catch (parseError) {
          toast.error(messagesLocale['ERROR_RESPONSE_NOT_PARSED']);
        }
      },
    },
  );

  const invalidateVaultQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_HARDCODED_REPOSITORY_FILES] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_REPOSITORY] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_OPEN_FILES] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDER] });
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_VAULT_FOLDERS] });
  }, [queryClient]);

  const handleCreateSuccess = useCallback(
    ({ includeSignatureQueries = false }: { includeSignatureQueries?: boolean } = {}) => {
      clearRevokedAccessModalState();
      invalidateQuery();
      invalidateVaultQueries();
      if (includeSignatureQueries) {
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SIGNATURES] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SELECTED_COMPANY] });
      }
      reset();
      handleClose();
      setCurrentStep(1);
    },
    [
      clearRevokedAccessModalState,
      handleClose,
      invalidateQuery,
      invalidateVaultQueries,
      queryClient,
      reset,
      setCurrentStep,
    ],
  );

  // The toast waits for the queued apply to settle (the list poll observes the reserved id
  // landing), so it agrees with the skeleton row leaving the list instead of firing mid-pending.
  // EXPIRED stays silent: it covers an append-merged fundraising/issuance create (a success
  // whose reserved id never lands) as well as a lost apply — neither outcome is provable.
  const handleCreateSettled = useCallback(
    (outcome: PendingMutationSettleOutcome) => {
      if (outcome === PendingMutationSettleOutcome.APPLIED) {
        toast.success(messagesLocale['NEW_EVENT_CREATED_SUCCESSFULLY']);
      }
      if (outcome === PendingMutationSettleOutcome.FAILED) {
        toast.error(messagesLocale['EVENT_CREATE_FAILED']);
      }
    },
    [messagesLocale],
  );

  const submitHandler = useCallback(
    async (data: FormSchema) => {
      const { initialStep, stepOne, optionalStep, stepTwo, companyIncorporation } = data;

      if (initialStep.type === EventFormType.COMPANY_INCORPORATION && companyIncorporation) {
        handleClose();
        try {
          const filesLinks = resolveCompanyIncorporationManualFileLinks(companyIncorporation.files);
          await createAsync({
            companyId,
            data: {
              type: EventTypeBackend.COMPANY_INCORPORATION,
              companyIncorporation: mapCompanyIncorporationFormToCreateDto(companyIncorporation),
              filesLinks,
              additionalNotes: companyIncorporation.additionalNotes ?? '',
            },
            onApplySettled: handleCreateSettled,
          });
          handleCreateSuccess();
        } catch {
          clearRevokedAccessModalState();
        }
        return;
      }

      const grantSharePlan = stepOne?.eventDetails?.grant?.sharePlan;

      const type = eventTypeMapping[initialStep.type];
      const sanitizedSignatoryAssignments = buildSanitizedGrantSignatoryAssignments(
        stepOne?.eventDetails?.grant?.signatoryAssignments,
      );

      const grant = initialStep.type === EventFormType.GRANT &&
        stepOne?.eventDetails?.grant?.grantItems &&
        stepOne.eventDetails.grant.grantItems.length > 0 && {
          items: stepOne.eventDetails.grant.grantItems.map(
            ({ grantDate, sharePlan, stakeholder, shares, files }) => ({
              date: toBackendDateFormat(grantDate),
              planId: sharePlan?.id || grantSharePlan?.id || '',
              stakeholderId: stakeholder.id,
              numbersOfGrants: shares,
              filesLinks:
                files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
            }),
          ),
          ...(stepOne.eventDetails.grant.signatureOption === GrantSignatureOption.SIGNATURE &&
            stepOne.eventDetails.grant.selectedTemplateId && {
              selectedTemplateId: stepOne.eventDetails.grant.selectedTemplateId,
              signatoryAssignments: sanitizedSignatoryAssignments,
            }),
        };

      const fundraisingDetails = stepOne?.eventDetails?.['fundraising-round'];
      // A 'new' valuation is sent inline so the backend creates both events in one mutation:
      // the async (SQS) create response carries no valuation id, so chaining a separate
      // valuation request first would silently drop the round.
      const fundraisingValuationRef =
        fundraisingDetails?.event?.id === 'new'
          ? {
              valuation: {
                name: fundraisingDetails.event.name ?? '',
                sharePrice: Number(fundraisingDetails.event.sharePrice),
                date: toBackendDateFormat(
                  fundraisingDetails.date || fundraisingDetails.closeDate || new Date(),
                ),
                method: stepOne?.eventDetails?.valuation?.valuationSelect,
              },
            }
          : { valuationId: fundraisingDetails?.event?.id };

      const fundraisingRound = initialStep.type === EventFormType.FUNDRAISING_ROUND &&
        stepOne.eventDetails?.['fundraising-round']?.enabled &&
        optionalStep?.['fundraising-round']?.investmentItems && {
          ...fundraisingValuationRef,
          sharePrice: stepOne.eventDetails['fundraising-round'].event.sharePrice,
          roundingSetting: stepOne.eventDetails['fundraising-round'].roundingSetting,
          isOpen: stepOne.eventDetails['fundraising-round'].isOpen,
          // `date` is the opening date in both states; the closing date has its own field.
          date: toBackendDateFormat(stepOne.eventDetails['fundraising-round']?.date || new Date()),
          closeDate: stepOne.eventDetails['fundraising-round']?.isOpen
            ? null
            : toBackendDateFormat(
                stepOne.eventDetails['fundraising-round']?.closeDate || new Date(),
              ),
          items: optionalStep?.['fundraising-round'].investmentItems.map(
            ({ investment, investmentDate, shareClass, stakeholder, files }) => ({
              stakeholderId: stakeholder.id,
              shareClassId: shareClass.id,
              investedValue: investment,
              date: toBackendDateFormat(investmentDate),
              filesLinks:
                files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
            }),
          ),
        };

      const shareIssuanceDetails = stepOne?.eventDetails?.['share-issuance'];
      // Same inline-valuation contract as fundraisingValuationRef above.
      const issuanceValuationRef =
        shareIssuanceDetails?.event?.id === 'new'
          ? {
              valuation: {
                name: shareIssuanceDetails.event.name ?? '',
                sharePrice: Number(shareIssuanceDetails.event.sharePrice),
                date: toBackendDateFormat(shareIssuanceDetails.date),
                method: stepOne?.eventDetails?.valuation?.valuationSelect,
              },
            }
          : { valuationId: shareIssuanceDetails?.event?.id };

      const issuance = initialStep.type === EventFormType.SHARE_ISSUANCE &&
        stepOne.eventDetails?.['share-issuance']?.enabled &&
        optionalStep?.['share-issuance']?.issuanceItems && {
          ...issuanceValuationRef,
          date: toBackendDateFormat(stepOne.eventDetails['share-issuance'].date),
          sharePrice: stepOne.eventDetails['share-issuance'].event.sharePrice,
          items: optionalStep?.['share-issuance']?.issuanceItems.map(
            ({ shares, shareClass, stakeholder, files, reverseVesting }) => ({
              stakeholderId: stakeholder.id,
              shareClassId: shareClass.id,
              sharesCount: shares,
              reverseVesting: mapReverseVestingFormToDto(reverseVesting),
              date: stepOne?.eventDetails['share-issuance']?.date
                ? toBackendDateFormat(stepOne.eventDetails['share-issuance'].date)
                : toBackendDateFormat(new Date()),
              filesLinks:
                files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
            }),
          ),
        };

      const buyback = initialStep.type === EventFormType.BUYBACK &&
        stepOne?.eventDetails?.buyback?.buyBackItems && {
          items: stepOne?.eventDetails.buyback?.buyBackItems.map(
            ({ date, shareClass, stakeholder, shares, files }) => ({
              date: toBackendDateFormat(date),
              stakeholderId: stakeholder.id,
              shareClassId: shareClass.id,
              sharesCount: shares,
              filesLinks:
                files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
            }),
          ),
        };

      const secondaries = initialStep.type === EventFormType.SECONDARIES &&
        stepOne?.eventDetails?.secondaries?.secondariesItems && {
          items: stepOne?.eventDetails?.secondaries?.secondariesItems.map(
            ({ date, shareClass, shares, shareValue, stakeholderFrom, stakeholderTo, files }) => ({
              date: toBackendDateFormat(date),
              fromStakeholderId: stakeholderFrom.id,
              toStakeholderId: stakeholderTo.id,
              shareClassId: shareClass.id,
              sharesCount: shares,
              sharesValue: Number(shareValue),
              filesLinks:
                files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
            }),
          ),
        };

      const conversion = initialStep.type === EventFormType.CONVERSION &&
        stepOne?.eventDetails?.conversion?.classConversionItems && {
          items: stepOne?.eventDetails?.conversion?.classConversionItems.map(
            ({ date, shares, shareClassFrom, shareClassTo, stakeholder }) => ({
              date: toBackendDateFormat(date),
              stakeholderId: stakeholder.id,
              fromShareClassId: shareClassFrom.id,
              toShareClassId: shareClassTo.id,
              sharesCount: shares,
            }),
          ),
        };

      const valuation = initialStep.type === EventFormType.VALUATION &&
        stepOne.eventDetails.valuation?.enabled && {
          name: stepOne?.eventDetails.valuation?.name,
          date: toBackendDateFormat(stepOne?.eventDetails?.valuation?.date),
          sharePrice: Number(stepOne?.eventDetails.valuation?.sharePrice),
          method: stepOne?.eventDetails.valuation?.valuationSelect,
        };

      if (
        initialStep.type === EventFormType.BULK_IMPORT_GRANT &&
        stepOne.eventDetails['bulk-import-grant']?.file &&
        stepOne.eventDetails.grant?.sharePlan?.id
      ) {
        uploadBulkGrant({
          file: stepOne.eventDetails['bulk-import-grant']!.file!,
          planId: stepOne.eventDetails.grant!.sharePlan!.id,
          filesLinks:
            stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
          additionalNotes: stepTwo?.additionalNotes,
          selectedTemplateId: stepOne.eventDetails.grant?.selectedTemplateId,
          signatoryAssignments: stepOne.eventDetails.grant?.signatoryAssignments,
        });
        invalidateQuery();
        handleClose();
        return;
      } else {
        const submitEvent = async () => {
          // Close the form immediately (optimistic): the skeleton row already reflects the create,
          // which finishes in the background; onError rolls the optimistic row back on failure.
          handleClose();
          try {
            await createAsync({
              companyId,
              data: {
                type: type,
                grant: grant || undefined,
                fundraisingRound: fundraisingRound || undefined,
                issuance: issuance || undefined,
                buyback: buyback || undefined,
                secondaries: secondaries || undefined,
                conversion: conversion || undefined,
                valuation: valuation || undefined,
                filesLinks:
                  stepTwo?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) ||
                  [],
                additionalNotes: stepTwo?.additionalNotes || '',
              },
              onApplySettled: handleCreateSettled,
            });
            queryClient.invalidateQueries({ queryKey: [QueryKey.GET_EVENT] });
            handleCreateSuccess({ includeSignatureQueries: true });
          } catch {
            clearRevokedAccessModalState();
          }
        };

        if (grant && 'selectedTemplateId' in grant) {
          const signerIds = Array.from(
            new Set([
              ...(stepOne?.eventDetails?.grant?.grantItems?.map(
                ({ stakeholder }) => stakeholder.id,
              ) ?? []),
              ...Object.values(sanitizedSignatoryAssignments).map(({ id }) => id),
            ]),
          );
          await runRevokedAccessCheck(signerIds, submitEvent);
          return;
        }

        await submitEvent();
      }
      return;
    },
    [
      uploadBulkGrant,
      invalidateQuery,
      createAsync,
      companyId,
      handleClose,
      handleCreateSuccess,
      handleCreateSettled,
      queryClient,
      runRevokedAccessCheck,
      clearRevokedAccessModalState,
    ],
  );

  const handleCloseModal = useCallback(() => {
    reset();
    handleClose();
    setCurrentStep(1);
  }, [reset, handleClose]);

  const handleNextStep = useCallback(async () => {
    const nextStep = currentStep + 1;
    if (nextStep === Object.keys(formSteps).length + 1) {
      handleSubmit(submitHandler, (errors) =>
        handleFormInvalidField(errors, describeInvalidFormField(errors)),
      )();
      return;
    }
    if (nextStep > Object.keys(formSteps).length) return;

    setCurrentStep(nextStep);
  }, [currentStep, formSteps, handleSubmit, submitHandler]);

  useEffect(() => {
    if (initialStep?.type) {
      if (
        initialStep.type === EventFormType.FUNDRAISING_ROUND ||
        initialStep.type === EventFormType.SHARE_ISSUANCE
      ) {
        setFormSteps([
          (props) => <InitialStep {...props} />,
          (props) => <StepOne {...props} />,
          (props) => <OptionalStep {...props} />,
          (props) => <StepTwo {...props} />,
          (props) => <FinalStep {...props} />,
        ]);
      } else if (initialStep.type === EventFormType.BULK_IMPORT_GRANT) {
        const withSignature = grantData?.signatureOption === GrantSignatureOption.SIGNATURE;

        if (withSignature) {
          setFormSteps([
            (props) => <InitialStep {...props} />,
            (props) => <BulkGrantStepFirst {...props} />,
            (props) => <StepOne {...props} />,
            (props) => <BulkGrantStepThird {...props} />,
            (props) => <BulkGrantStepFourth {...props} />,
            (props) => <StepTwo {...props} />,
            (props) => <FinalStep {...props} />,
          ]);
        } else {
          setFormSteps([
            (props) => <InitialStep {...props} />,
            (props) => <BulkGrantStepFirst {...props} />,
            (props) => <StepOne {...props} />,
            (props) => <StepTwo {...props} />,
            (props) => <FinalStep {...props} />,
          ]);
        }
      } else if (initialStep.type === EventFormType.COMPANY_INCORPORATION) {
        setFormSteps([
          (props) => <InitialStep {...props} />,
          (props) => <CompanyIncorporationStepOne {...props} />,
          (props) => <CompanyIncorporationStepTwo {...props} />,
          (props) => <CompanyIncorporationStepThree {...props} />,
          (props) => <CompanyIncorporationFinalStep {...props} />,
        ]);
      } else if (initialStep.type === EventFormType.GRANT) {
        if (!stepOne?.eventDetails?.grant) {
          setValue('stepOne.eventDetails.grant.enabled', true);
        }
        const withSignature = grantData?.signatureOption === GrantSignatureOption.SIGNATURE;
        if (withSignature) {
          setFormSteps([
            (props) => <InitialStep {...props} />,
            (props) => <GrantStepFirst {...props} />,
            (props) => <StepOne {...props} />,
            (props) => <GrantStepThird {...props} />,
            (props) => <GrantStepFourth {...props} />,
            (props) => <StepTwo {...props} />,
            (props) => <FinalStep {...props} />,
          ]);
        } else {
          setFormSteps([
            (props) => <InitialStep {...props} />,
            (props) => <GrantStepFirst {...props} />,
            (props) => <StepOne {...props} />,
            (props) => <StepTwo {...props} />,
            (props) => <FinalStep {...props} />,
          ]);
        }
      } else {
        setFormSteps([
          (props) => <InitialStep {...props} />,
          (props) => <StepOne {...props} />,
          (props) => <StepTwo {...props} />,
          (props) => <FinalStep {...props} />,
        ]);
      }
    }
  }, [grantData?.signatureOption, initialStep?.type, setValue, stepOne?.eventDetails?.grant]);

  useEffect(() => {
    setCurrentStep((prev) => Math.min(prev, formSteps.length));
  }, [formSteps.length]);

  const handlePrevStep = useCallback(() => {
    const prevStep = currentStep - 1;
    if (prevStep < 1) return;
    if (prevStep === 1) {
      const snapshot = getValues();
      reset({
        initialStep: snapshot.initialStep,
        optionalStep: undefined,
        stepOne: undefined,
        stepTwo: undefined,
        companyIncorporation: undefined,
      });
      clearErrors();
    }
    setCurrentStep(prevStep);
  }, [clearErrors, currentStep, getValues, reset]);

  const grantPlanName = grantData?.sharePlan?.name?.trim();
  const showGrantSubtitle =
    initialStep?.type === EventFormType.GRANT &&
    Boolean(grantPlanName) &&
    grantData?.signatureOption != null;

  return (
    <>
      <SignatureRestoreAccessConfirmModal
        isOpen={revokedAccessSigners.length > 0}
        onClose={clearRevokedAccessModalState}
        onConfirm={handleConfirmRestore}
        signers={revokedAccessSigners}
      />

      <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
        <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
          <div className="flex min-w-0 flex-col">
            <span className="text-xl font-[550] text-gray-700">
              {initialStep?.type ? (
                EventTypeTitle[initialStep.type]
              ) : (
                <AppFormattedMessage id="ADD_EVENT" />
              )}
            </span>
            {showGrantSubtitle && (
              <span className="block text-xs font-[450] text-gray-500">
                <AppFormattedMessage
                  id={
                    grantData?.signatureOption === GrantSignatureOption.SIGNATURE
                      ? 'GRANT_EVENT_MODAL_SUBTITLE_SIGNATURE'
                      : 'GRANT_EVENT_MODAL_SUBTITLE_NO_SIGNATURE'
                  }
                  values={{ planName: grantPlanName }}
                />
              </span>
            )}
          </div>
          <CloseModalButton onClose={handleCloseModal} />
        </div>
        {currentStep !== 1 && (
          <div className="flex w-full items-center gap-[5px] bg-white px-6 pb-6">
            {formSteps.slice(1).map((_, i) => (
              <div
                className={twMerge(
                  'h-1 w-full rounded-[25px]',
                  i + 1 < currentStep ? 'bg-[#12B76A]' : 'bg-gray-100',
                  i + 1 === currentStep &&
                    currentStep === formSteps.length &&
                    'bg-gradient-to-r from-forest-500 to-forest-300',
                )}
                key={`${i}_${companyId}`}
              />
            ))}
          </div>
        )}

        <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
          {formSteps[Math.min(currentStep - 1, formSteps.length - 1)]({
            companyId,
            issuedShares,
            formData: getValues,
            nextFormStep: handleNextStep,
            prevFormStep: handlePrevStep,
            setFormData: setValue,
            control,
            clearErrors,
            handleCloseModal,
            setError,
            filedState: getFieldState,
            errors,
            reset,
            isLoading: isPending || isCheckingRevokedAccess || revokedAccessSigners.length > 0,
            event: null,
          })}
        </div>
      </div>
    </>
  );
};
